// Konfigurasi umum aplikasi (nilai tampilan/branding hidup di site_settings)

/** Nomor WhatsApp admin (format internasional tanpa +, untuk wa.me) */
export const ADMIN_WHATSAPP =
  process.env.NEXT_PUBLIC_ADMIN_WHATSAPP ?? "6281234567890";

/**
 * Domain email dummy untuk akun yang mendaftar via nomor WhatsApp.
 * AWAS: email auth tersimpan sebagai 08xx@<domain> — mengganti nilai ini pada
 * deployment yang SUDAH punya pengguna akan mematahkan login lama. Deployment
 * baru (copy untuk client lain) boleh menyetel env NEXT_PUBLIC_PHONE_EMAIL_DOMAIN.
 */
export const PHONE_EMAIL_DOMAIN =
  process.env.NEXT_PUBLIC_PHONE_EMAIL_DOMAIN ?? "galea.id";

/**
 * Escape wildcard LIKE (`%` `_` `\`) agar `ilike` mencocokkan LITERAL, bukan
 * pola. Tanpa ini email ber-`_` (umum) diperlakukan sebagai wildcard sehingga
 * satu input bisa cocok ke akun ORANG LAIN — karena itu ia tinggal di satu
 * tempat dan dipakai bersama semua jalur lookup identitas (login, reset
 * password), bukan disalin per-route.
 */
export function escapeLikeLiteral(value: string): string {
  return value.replace(/[\\%_]/g, (c) => `\\${c}`);
}

/**
 * Normalisasi nomor WhatsApp ke bentuk KANONIK — nilai yang disimpan di
 * `customers.phone`/`profiles.phone` sekaligus local part email dummy:
 *
 *   Indonesia   → `08xxxxxxxxxx`  (08xx, 62xx, +62xx, 0062xx semua ke sini)
 *   luar negeri → `<kode negara><nomor>` digit E.164 TANPA "+" (mis. 60123456789)
 *
 * Nomor Indonesia keluar PERSIS seperti sebelum dukungan luar negeri ada —
 * identitas `08xx@<domain toko>` yang sudah hidup di `auth.users` tidak boleh
 * bergeser satu karakter pun.
 *
 * Tanda "+" sengaja DIBUANG untuk nomor asing: nilai ini masuk ke local part
 * email auth, dan satu nomor yang bisa tersimpan dua rupa (`+60…` vs `60…`)
 * menembus unique (store_id, phone) sehingga lahir akun kembar. Tak ada risiko
 * tabrakan dengan nomor lama: bentuk Indonesia selalu diawali "0", sedangkan
 * kode negara E.164 tidak pernah.
 */
export function normalizePhone(raw: string): string {
  const kept = raw.replace(/[^0-9+]/g, "");
  // Penanda internasional EKSPLISIT: "+" (E.164) atau "00" (prefix dial-out).
  const intl = kept.startsWith("+")
    ? kept.slice(1)
    : kept.startsWith("00")
      ? kept.slice(2)
      : null;
  if (intl !== null) {
    const digits = intl.replace(/[^0-9]/g, "");
    // Kode negara Indonesia tetap turun ke bentuk lokal 08xx.
    return digits.startsWith("62") ? "0" + digits.slice(2) : digits;
  }
  if (kept.startsWith("62")) return "0" + kept.slice(2);
  return kept;
}

/**
 * Konversi nomor WA ke email dummy Supabase: `<nomor kanonik>@<domain>` —
 * `081234567890@toko.id` untuk Indonesia, `60123456789@toko.id` untuk nomor
 * luar negeri (lihat `normalizePhone`).
 * `domain` = dummy_email_domain TOKO pemilik akun (mig 3190, A1 Fase 6) —
 * WAJIB di-thread di jalur multi-tenant; default PHONE_EMAIL_DOMAIN hanya
 * untuk pemanggil legacy single-store.
 */
export function phoneToEmail(
  phone: string,
  domain: string = PHONE_EMAIL_DOMAIN
): string {
  const d = domain.trim().toLowerCase() || PHONE_EMAIL_DOMAIN;
  return `${normalizePhone(phone)}@${d}`;
}

/** Nomor Indonesia baku: 08 + minimal 8 digit. */
const ID_PHONE_RE = /^08\d{8,}$/;

/**
 * Nomor luar negeri bentuk E.164 tanpa "+": kode negara 1–9 di depan, total
 * 8–15 digit (batas ITU-T E.164). Tak mungkin cocok ke `08xx` yang diawali "0".
 */
const INTL_PHONE_RE = /^[1-9]\d{7,14}$/;

/**
 * Validasi nomor WA INDONESIA: minimal 10 digit, format 08xx / 62xx / +62xx.
 *
 * SENGAJA tetap ketat Indonesia-saja — ia dipakai jalur yang memang cuma
 * melayani nomor lokal (nomor WA toko, form lead platform, akun admin).
 * Untuk jalur yang menerima pelanggan luar negeri pakai
 * `isAcceptablePhoneInput` (ketikan mentah) atau `isCanonicalPhone` (nilai
 * yang sudah tersimpan).
 */
export function isValidPhone(raw: string): boolean {
  return ID_PHONE_RE.test(normalizePhone(raw));
}

/**
 * Apakah `value` sudah berbentuk nomor KANONIK yang sah — `08xx` Indonesia
 * ATAU E.164 luar negeri? Menilai nilai yang SUDAH tersimpan (kolom
 * `customers.phone`/`profiles.phone`, local part email dummy), bukan ketikan
 * mentah pengguna: di sini tidak ada lagi "+" atau spasi untuk dibersihkan.
 */
export function isCanonicalPhone(value: string | null | undefined): boolean {
  const v = (value ?? "").trim();
  return ID_PHONE_RE.test(v) || INTL_PHONE_RE.test(v);
}

/**
 * Apakah KETIKAN pengguna boleh diterima sebagai nomor WhatsApp? Indonesia
 * seperti biasa (08xx / 62xx / +62xx); luar negeri WAJIB membawa penanda
 * internasional eksplisit — "+" atau "00".
 *
 * Digit telanjang non-nol tetap DITOLAK dengan sengaja: `81234567890` jauh
 * lebih mungkin orang Indonesia yang lupa "0" daripada nomor Jepang (+81),
 * dan menebaknya berarti menanam identitas auth di ruang nomor yang salah —
 * kesalahan yang baru ketahuan saat pemiliknya tak bisa login.
 */
export function isAcceptablePhoneInput(raw: string): boolean {
  const trimmed = (raw ?? "").trim();
  if (!trimmed) return false;
  if (isValidPhone(trimmed)) return true;
  const kept = trimmed.replace(/[^0-9+]/g, "");
  if (!kept.startsWith("+") && !kept.startsWith("00")) return false;
  return isCanonicalPhone(normalizePhone(trimmed));
}

/**
 * Deteksi apakah input login berupa nomor HP (bukan email): awalan 0/62/+62
 * diikuti minimal 8 digit, ATAU nomor internasional berpenanda "+"/"00".
 * Dipakai untuk memilih jalur resolusi email auth.
 *
 * Perhatikan ini menjawab "BERBENTUK nomor?", bukan "nomor SAH?" — input
 * berpola nomor yang panjangnya kurang tetap dijawab true supaya pesan
 * errornya menyalahkan nomornya, bukan mengarahkan pengguna ke jalur email.
 */
export function looksLikePhone(input: string): boolean {
  const cleaned = input.replace(/[\s\-()]/g, "");
  const intl = cleaned.startsWith("+")
    ? cleaned.slice(1)
    : /^00\d/.test(cleaned)
      ? cleaned.slice(2)
      : null;
  if (intl !== null) return INTL_PHONE_RE.test(intl);
  // Deretan digit telanjang sepanjang nomor telepon ikut dinilai BERBENTUK
  // nomor walau `isAcceptablePhoneInput` nanti menolaknya. Tanpa ini input
  // seperti `60123456789` jatuh ke jalur email dan pengguna cuma menerima
  // "kata sandi salah" — buntu; dengan ini ia menerima petunjuk formatnya.
  return (
    /^(62|0)[0-9]{8,}$/.test(cleaned) || /^[0-9]{8,15}$/.test(cleaned)
  );
}

/**
 * Sanitasi parameter redirect `?next=` menjadi path INTERNAL yang aman.
 *
 * `window.location.href = next` tanpa sanitasi = open redirect + XSS: link
 * `/login?next=https://evil.com`, `next=//evil.com` (protocol-relative), atau
 * `next=javascript:...` akan dieksekusi setelah login sukses. Hanya path yang
 * diawali SATU "/" yang diterima; karakter kedua "/" atau "\" ditolak karena
 * browser menormalkan `/\evil.com` menjadi `//evil.com`.
 */
export function safeInternalPath(
  raw: string | null | undefined,
  fallback = "/"
): string {
  if (!raw) return fallback;
  const trimmed = raw.trim();
  if (!trimmed.startsWith("/")) return fallback;
  if (trimmed.length > 1 && (trimmed[1] === "/" || trimmed[1] === "\\")) {
    return fallback;
  }
  return trimmed;
}

/**
 * Bangun daftar kandidat email auth Supabase (urut prioritas) dari satu input
 * login, untuk dicoba berurutan hingga salah satu berhasil.
 *
 * Model data: identitas auth reseller SELALU email dummy phone-derived
 * (`08xx@<domain>`) — email asli cuma field kontak di customers/profiles.
 * Admin boleh dibuat dengan email asli sebagai identitas auth. Maka:
 *  - Input nomor HP → satu kandidat: email dummy phone-derived.
 *  - Input email → kandidat "apa adanya" (akun ber-email auth nyata / email
 *    dummy diketik langsung) DAN, bila `resolvedPhone` ditemukan dari email
 *    kontak, email dummy phone-derived (reseller login via email kontaknya).
 *
 * `preferRawEmail` menaruh kandidat email-apa-adanya di depan (dipakai scope
 * admin yang identitasnya email nyata) agar akun admin tak keliru cocok ke
 * akun pelanggan ber-email kontak sama.
 *
 * `domain` (A1 Fase 6) = dummy_email_domain TOKO pemilik domain request —
 * jalur login multi-tenant WAJIB meneruskannya supaya kandidat phone-derived
 * dibentuk di ruang identitas toko yang benar.
 */
export function buildLoginAuthEmails(
  input: string,
  resolvedPhone?: string | null,
  preferRawEmail = false,
  domain: string = PHONE_EMAIL_DOMAIN
): string[] {
  const trimmed = input.trim();
  if (looksLikePhone(trimmed)) {
    return [phoneToEmail(normalizePhone(trimmed), domain)];
  }
  const rawEmail = trimmed.toLowerCase();
  // `isCanonicalPhone` (bukan `isValidPhone`): nomor yang tersimpan bisa E.164
  // luar negeri — pelanggan asing pun harus bisa login lewat email kontaknya.
  const phoneEmail =
    resolvedPhone && isCanonicalPhone(resolvedPhone)
      ? phoneToEmail(resolvedPhone, domain)
      : null;
  const ordered = preferRawEmail ? [rawEmail, phoneEmail] : [phoneEmail, rawEmail];
  return [...new Set(ordered.filter((e): e is string => Boolean(e)))];
}
